import {
  ActionError,
  Button,
  EmptyState,
  FilterSegments,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  titleCase,
  toast,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import {
  usePractitionerAction,
  usePractitioners,
  type Practitioner,
  type PractitionerFilter,
} from './api'
import { councilLabel, professionLabel } from './labels'

/**
 * Prestadores da clínica (docs/areas/administracao.md, "Prestadores"; RN-ADM-22 a 24).
 * Arquétipo: lista de trabalho. Quem responde tecnicamente pelo procedimento; recepção e prestador
 * leem, sem ações. Arquivar mora no cadastro; aqui o arquivado volta por "restaurar".
 */

const FILTERS: PractitionerFilter[] = ['active', 'archived']

export function PractitionersPage() {
  const session = useSession()
  const navigate = useNavigate()
  const canManage = session.canManageClinic

  const { filter, setFilter } = useStoredFilter('practitioners', FILTERS, 'active')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounced(search.trim())
  const query = usePractitioners({ filter, search: debouncedSearch || undefined, page })
  const counts = query.data?.meta.counts

  const action = usePractitionerAction()
  const [failure, setFailure] = useState<{ title: string; error: unknown } | null>(null)

  const options: FilterSegmentOption<PractitionerFilter>[] = [
    { value: 'active', label: 'Ativos', count: counts?.active },
    { value: 'archived', label: 'Arquivados', count: counts?.archived },
  ]

  function restore(practitioner: Practitioner) {
    const name = titleCase(practitioner.name)
    setFailure(null)
    action.mutate(
      { id: practitioner.id, action: 'restore' },
      {
        onSuccess: () => toast(`Cadastro de ${name} restaurado — volta à lista de profissionais`),
        onError: (error) => setFailure({ title: `Não foi possível restaurar "${name}"`, error }),
      },
    )
  }

  const practitioners = query.data?.data ?? []
  const registered = (counts?.active ?? 0) + (counts?.archived ?? 0)
  const isEmptyList = !query.isPending && !debouncedSearch && registered === 0
  const showSearch = registered > 20 || search !== ''

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Administração' }, { label: 'Prestadores' }]}
        actions={
          canManage ? (
            <Button size="sm" asChild>
              <Link to={routes.admin.newPractitioner}>+ Criar prestador</Link>
            </Button>
          ) : null
        }
      />
      <Page wide>
        <PageTitle
          title="Prestadores"
          subtitle="Quem responde tecnicamente pelo procedimento. Conselho e registro entram no documento assinado — é o que prova habilitação."
        />

        {failure ? (
          <ActionError
            className="mt-6"
            title={failure.title}
            description={`${errorMessage(failure.error)} Nada mudou no cadastro.`}
            onDismiss={() => setFailure(null)}
          />
        ) : null}

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar os prestadores"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : isEmptyList ? (
          <EmptyState
            className="mt-6"
            title="Nenhum prestador ainda"
            description={
              canManage
                ? 'Prestador é quem responde tecnicamente pelo procedimento. Nome, conselho e registro entram em todo documento assinado; sem eles, o contrato não prova habilitação.'
                : 'Prestador é quem responde tecnicamente pelo procedimento. Nome, conselho e registro entram em todo documento assinado. Quem cadastra é a gestora da clínica.'
            }
            action={
              canManage ? (
                <Button asChild>
                  <Link to={routes.admin.newPractitioner}>Criar o primeiro prestador</Link>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <FilterSegments
                label="Filtrar prestadores"
                options={options}
                value={filter}
                onChange={(value) => {
                  setFilter(value)
                  setPage(1)
                }}
              />
              {showSearch ? (
                <SearchInput
                  className="tablet:max-w-[300px]"
                  value={search}
                  onChange={(value) => {
                    setSearch(value)
                    setPage(1)
                  }}
                  placeholder="Buscar por nome, profissão ou registro"
                  aria-label="Buscar prestador"
                />
              ) : null}
            </div>

            <div className="mt-4">
              {query.isPending ? (
                <TableSkeleton rows={4} columns={4} />
              ) : practitioners.length === 0 ? (
                <EmptyState
                  {...emptyCopy(filter, debouncedSearch)}
                  action={
                    debouncedSearch ? (
                      <Button variant="outline" onClick={() => setSearch('')}>
                        Limpar busca
                      </Button>
                    ) : filter === 'archived' ? (
                      <Button variant="outline" onClick={() => setFilter('active')}>
                        Ver ativos
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={() => setFilter('archived')}>
                        Ver arquivados
                      </Button>
                    )
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead>Nome</TableHead>
                      <TableHead>Profissão</TableHead>
                      <TableHead>Conselho</TableHead>
                      <TableHead>Registro</TableHead>
                      {canManage ? <TableHead aria-label="Ações" /> : null}
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {practitioners.map((practitioner) => (
                      <TableRow
                        key={practitioner.id}
                        onClick={
                          canManage
                            ? () => navigate(routes.admin.practitioner(practitioner.id))
                            : undefined
                        }
                      >
                        <TableCell primary>{titleCase(practitioner.name)}</TableCell>
                        <TableCell label="Profissão" muted>
                          {professionLabel(practitioner)}
                        </TableCell>
                        <TableCell label="Conselho">
                          {councilLabel({
                            code: practitioner.council,
                            name: practitioner.councilName,
                          })}
                        </TableCell>
                        <TableCell label="Registro">
                          {practitioner.registrationNumber ?? '—'}
                        </TableCell>
                        {canManage ? (
                          <TableCell>
                            <RowActions>
                              {practitioner.archivedAt ? (
                                <button type="button" onClick={() => restore(practitioner)}>
                                  restaurar
                                </button>
                              ) : (
                                <Link to={routes.admin.practitioner(practitioner.id)}>editar</Link>
                              )}
                            </RowActions>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {query.data && practitioners.length > 0 ? (
                <ListFooter
                  meta={query.data.meta}
                  noun={['prestador', 'prestadores']}
                  note="nomes exibidos em caixa de título mesmo quando digitados em minúsculas — o nome vai para o contrato"
                  onPage={setPage}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>
    </>
  )
}

/** O vazio de cada recorte: o que é, por que está vazio. */
function emptyCopy(filter: PractitionerFilter, search: string) {
  if (search) {
    return {
      title: `Nenhum prestador para "${search}"`,
      description: 'A busca cobre nome, profissão, conselho e registro.',
    }
  }
  if (filter === 'archived') {
    return {
      title: 'Nenhum prestador arquivado',
      description: 'Prestador arquivado aparece aqui e volta a qualquer momento.',
    }
  }
  return {
    title: 'Nenhum prestador ativo',
    description:
      'Todos os prestadores estão arquivados. Sem um prestador ativo, não há quem responda pelo procedimento em um documento novo.',
  }
}
